<template>
<div id="purchasableItem">
  <h2>Purchasable Item #{{ purchasableItemId }}</h2>

  <purchasable-item-nav
    :purchasableItemId="purchasableItemId"
  ></purchasable-item-nav>

  <h3>Item Details</h3>
  <table class="table">
    <tr>
      <td>
        Name
      </td>
      <td>
        {{ name }}

        <h5
          class="d-inline"
          v-if="free_trial"
        >
          <span class="badge badge-secondary badge-success">Free Trial</span>
        </h5>
      </td>
    </tr>
    <tr>
      <td>
        Description
      </td>
      <td>
        {{ description }}
      </td>
    </tr>
    <tr>
      <td>
        Price
      </td>
      <td>
        ${{ price }}
      </td>
    </tr>
    <tr>
      <td>
        Featured?
      </td>
      <td>
        <span
          class="badge badge-sm badge-primary"
        >
          {{ featured ? 'Yes' : 'No' }}
        </span>
      </td>
    </tr>
    <tr>
      <td>
        Subscription?
      </td>
      <td>
        <span
          class="badge badge-sm badge-primary"
        >
          {{ subscription ? 'Yes' : 'No' }}
        </span>
      </td>
    </tr>
    <tr
      v-if="subscription"
    >
      <td>
        Recurring Subscription?
      </td>
      <td>
        <span
          class="badge badge-sm badge-primary"
        >
          {{ recurring ? 'Yes' : 'No' }}
        </span>
      </td>
    </tr>
    <tr
      v-if="subscription"
    >
      <td>
        Renewable Subscription?
      </td>
      <td>
        <span
          class="badge badge-sm badge-primary"
        >
          {{ renewal ? 'Yes' : 'No' }}
        </span>
      </td>
    </tr>
    <tr
      v-if="subscription"
    >
      <td>
        Subscription Interval
      </td>
      <td>
        {{ subscription_interval }}
      </td>
    </tr>
    <tr>
      <td>
        Slug
      </td>
      <td>
        {{ slug }}
      </td>
    </tr>
    <tr>
      <td>
        Direct Purchase Link
      </td>
      <td>
        <a
          :href="purchase_url"
          target="_blank"
        >
          {{ purchase_url }}
        </a>
      </td>
    </tr>
    <tr>
      <td>
        UDIDs
      </td>
      <td>
        {{ udids }}
      </td>
    </tr>
    <tr>
      <td>
        Created At
      </td>
      <td>
        {{ formattedDate(created_at) }}
      </td>
    </tr>
    <tr>
      <td>
        Available At
      </td>
      <td>
        {{ formattedDate(starts_at) }}
      </td>
    </tr>
    <tr>
      <td>
        Available Until
      </td>
      <td>
        {{ formattedDate(ends_at) }}
      </td>
    </tr>
    <tr>
      <td>
        Visible?
      </td>
      <td>
        <span
          class="badge badge-sm badge-primary"
        >
          {{ visible ? 'Yes' : 'No' }}
        </span>
      </td>
    </tr>
    <tr>
      <td>
        Visible At
      </td>
      <td>
        {{ formattedDate(visible_at) }}
      </td>
    </tr>
    <tr
      v-if="subscription"
    >
      <td>
        Subscription Valid At
      </td>
      <td>
        {{ formattedDate(subscription_starts_at) }}
      </td>
    </tr>
    <tr
      v-if="subscription"
    >
      <td>
        Subscription Valid Until
      </td>
      <td>
        {{ formattedDate(subscription_ends_at) }}
      </td>
    </tr>
    <tr>
      <td>
        Tags
      </td>
      <td v-if="tags.length">
        <span
          v-for="tag in tags"
          v-bind:key="tag.id"
          v-if="tag.attributes"
        >
          <router-link
            :to="{
              name: 'tag',
              params: {
                tagId: tag.id
              }
            }"
            class="btn btn-primary btn-sm tag mr-2"
          >
            {{ tag.attributes.name }}
          </router-link>
        </span>
      </td>
      <td v-else>
        No tags
      </td>
    </tr>
  </table>

  <div v-if="puzzles">

    <hr />

    <h3>Puzzles</h3>

    <ol v-for="puzzle in puzzles">
      <li>
        <router-link :to="{ name: 'puzzle', params: { puzzleId: puzzle.id }}">
          {{ puzzle.name }}
        </router-link>
      </li>
    </ol>
  </div>

  <hr />

  <div>
    <h3>Attachments</h3>

    <ol v-if="purchasable_item_attachments">
      <li v-for="attachment in purchasable_item_attachments">
        <a
          href="#"
          :data-attachment-id="attachment.id"
          :data-attachment-mimetype="attachment.attributes.mimetype"
          v-on:click.prevent="downloadAttachment"
        >
          {{ attachment.attributes.filename }} - {{ Math.ceil(attachment.attributes.byte_size / 1024) }}kb
          ({{ attachment.attributes.mimetype }})
        </a>
      </li>
    </ol>
    <p v-else>
      No attachments
    </p>
  </div>

  <hr />

  <div v-if="subscription">
    <h3>Subscription Details</h3>

    <p>Subscription Interval: {{ subscription_interval }}</p>

    <p>Subscription Sandbox ID: {{ external_payment_sandbox_id }}</p>

    <p>Subscription Production ID: {{ external_payment_production_id }}</p>
  </div>

  <p>
    <button
      class="btn btn-danger btn-block"
      v-on:click.prevent="deleteItem"
    >
      Delete Purchasable Item
    </button>
  </p>

</div>
</template>

<script>
import PurchasableItemNav from '@/components/PurchasableItemNav'

import FlashStore from '@/stores/FlashStore'
import PurchasableItemStore from '@/stores/PurchasableItemStore'

export default {
  data () {
    return PurchasableItemStore.state
  },

  props: ['purchasableItemId'],

  components: {
    'purchasable-item-nav': PurchasableItemNav
  },

  created () {
    this.get()
  },

  methods: {
    formattedDate (d) {
      return this.$root.displayDateTimeFmt(d)
    },

    get () {
      this.apiGet(
        ('/purchasable_items/' + this.purchasableItemId),
        PurchasableItemStore,
        true
      ).then((resp) => {
        const puzzles = this.relationships.puzzles
        this.purchasable_item_attachments = this.purchasable_item_attachments

        if (puzzles && puzzles.length > 0) {
          this.$http.get(this.apiUrl('/puzzles_by_ids'), {
            params: {
              ids: puzzles.map((p) => { return p.id })
            }
          }).then((resp) => {
            this.puzzles = this.processSuccess(resp)
          })
        }
      })
    },

    deleteItem () {
      if (confirm('This will delete the purchasable item, including purchase history, and cannot be undone. Are you sure you want to delete the purchasable item?')) {
        this.$http.delete(
          this.apiUrl('/purchasable_items/' + this.purchasableItemId, true)
        ).then((resp) => {
          this.$router.push({
            name: 'purchasable_items'
          })

          FlashStore.result('This purchasable item has been deleted.')
        }, this.processErrors)
      }
    },

    downloadAttachment (ev) {
      const target = ev.target
      const mimetype = target.dataset.mimetype

      this.$http.get(this.apiUrl('/purchasable_item_attachments/' + target.dataset.attachmentId + '/download', true), {
        headers: {
          'Accept': mimetype
        },
        responseType: 'blob'
      }).then((response) => {
        let headers = response.headers

        let blob = new Blob([response.bodyBlob], {
          type: headers['Content-Type']
        })

        let link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        link.download = response.headers.get('filename')
        link.click()
      }, this.processErrors)
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/PurchasableItem.vue